import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { noop } from 'lodash';

import { Icon, Label } from 'shared/components';
import { grayMedium, redMedium } from 'styleVars/getColors';
import requestsStatuses from 'modules/common/constants/requestsStatuses';

import theme from './Checkbox.scss';

const propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  labelPosition: PropTypes.string,
  labelSuffix: PropTypes.any,
  labelSize: PropTypes.oneOf(['small', 'medium', 'large']),
  checkedIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  hasError: PropTypes.bool,
  color: PropTypes.string,
  status: PropTypes.oneOf([
    requestsStatuses.default,
    requestsStatuses.failure,
    requestsStatuses.pending,
    requestsStatuses.success,
  ]),
  enablePropagation: PropTypes.bool,
  onClick: PropTypes.func,
  labelClassName: PropTypes.string,
  iconClassName: PropTypes.string,
  counter: PropTypes.number,
  inputRef: PropTypes.func,
  tabIndex: PropTypes.number,
};

const defaultProps = {
  id: undefined,
  className: '',
  labelPosition: 'right',
  labelSuffix: null,
  labelSize: 'medium',
  checked: false,
  disabled: false,
  checkedIcon: 'check',
  labelClassName: '',
  iconClassName: '',
  color: undefined,
  status: requestsStatuses.default,
  onClick: noop,
  enablePropagation: false,
  counter: null,
  hasError: false,
  inputRef: undefined,
  tabIndex: 0,
};

const Checkbox = ({
  id,
  className,
  label,
  labelPosition,
  labelClassName,
  disabled,
  labelSuffix,
  labelSize,
  checkedIcon,
  iconClassName,
  color,
  counter,
  checked,
  hasError,
  onClick,
  enablePropagation,
  status,
  inputRef,
  tabIndex,
}) => {
  const handleCheckboxClick = (e) => {
    if (!enablePropagation) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (disabled) return;

    onClick(!checked);
  };

  const handleKeyPress = (e) => {
    if (e.key === ' ' ||
      e.code === 'Space' ||
      e.keyCode === 32
    ) {
      return handleCheckboxClick(e);
    }
  };

  const colored = (status === requestsStatuses.failure || hasError)
    ? redMedium
    : status === requestsStatuses.pending
      ? grayMedium
      : color;

  return (
    <div
      className={ classnames(theme.root, className, { [theme.disabled]: disabled }) }
      onClick={ handleCheckboxClick }
    >
      <Label
        id={ `checkbox-${ id }-label` }
        htmlFor={ `checkbox-${ id }` }
        className={ classnames(theme.checkboxLabel, labelClassName, {
          [theme.disabled]: disabled,
        }) }
        label={ label }
        labelSuffix={ labelSuffix }
        size={ labelSize }
        tabIndex={ tabIndex }
        role="checkbox"
        aria-checked={ checked }
        aria-labelledby={ `checkbox-${ id }-label` }
        onKeyDown={ handleKeyPress }
      />
      <input
        type="checkbox"
        id={ `checkbox-${ id }` }
        checked={ checked }
        onChange={ noop }
        ref={ inputRef }
      />
      <div
        className={ classnames(theme.iconWrap, { [theme[labelPosition]]: labelPosition }) }
      >
        <div
          className={ classnames(theme.iconContainer, iconClassName, {
            [theme.checked]: checked,
            [theme.error]: hasError,
          }) }
          style={{ background: checked && !counter ? colored : 'transparent', borderColor: colored }}
        >
          <If condition={ !!counter }>
            <div className={ theme.counter }> { counter } </div>
          </If>
          <If condition={ !counter }>
            <Choose>
              <When condition={ typeof checkedIcon === 'string' } >
                <Icon name={ checkedIcon } />
              </When>
              <When condition={ typeof checkedIcon === 'object' }>
                { checkedIcon }
              </When>
              <Otherwise>
                <Icon name={ defaultProps.checkedIcon } />
              </Otherwise>
            </Choose>
          </If>
        </div>
      </div>
    </div>
  );
};

Checkbox.propTypes = propTypes;
Checkbox.defaultProps = defaultProps;

export default Checkbox;
